import React from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import {
  Sell as SellIcon,
  ShoppingCart as CartIcon
} from '@mui/icons-material';
import { MarketplaceListing, UserNft } from '../types';
import {
  cardSx,
  nameSx,
  kickerSx,
  subSx,
  cyanBtnSx,
  priceValueSx,
  MONO,
  StatPill,
  Pill,
  NftArt
} from './cardKit';
import { cardTitle } from '../../NFTCollection/components/CardInspectModal';

type Props = {
  data: any; // keep as-is to avoid churn
  isListing?: boolean;
  listing?: MarketplaceListing;
  cloudfrontBase?: string;
  formatRarity: (rarity: string | undefined | null) => string;
  getRarityColor: (rarity: string | undefined | null) => string;
  currentUsername: string;
  onBuyNow: (listing: MarketplaceListing) => void;
  onListForSale: (userNft: UserNft) => void;
};

export const NftCard: React.FC<Props> = ({
  data,
  isListing = false,
  listing,
  cloudfrontBase,
  formatRarity,
  getRarityColor,
  currentUsername,
  onBuyNow,
  onListForSale
}) => {
  if (!data) return null;

  const nft = isListing ? data : data.nft;
  if (!nft) {
    return (
      <Card sx={cardSx}>
        <CardContent>
          <Typography sx={{ color: '#8ea3b3' }}>
            Unknown Digital Collectible
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const nftName = nft.name || 'Unknown Digital Collectible';
  const nftType = nft.type || 'Unknown';
  const nftRarity = nft.rarity || 'common';
  const nftFaction = nft.faction || 'Unknown';
  const rarityColor = getRarityColor(nftRarity);

  const imgSrc = cloudfrontBase
    ? `${cloudfrontBase}/${nftType}/${nftName}/${nftRarity}.png`
    : '';

  return (
    <Card sx={cardSx}>
      <NftArt
        imgSrc={imgSrc}
        alt={nftName}
        rarityColor={rarityColor}
        topRight={<Pill color={rarityColor}>{formatRarity(nftRarity)}</Pill>}
      />

      <CardContent
        sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 2 }}
      >
        <Typography sx={nameSx}>{cardTitle(nftName)}</Typography>
        <Typography sx={kickerSx}>
          {nftType} · {nftFaction}
        </Typography>

        {(nft.attack !== undefined || nft.defence !== undefined) && (
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25 }}>
            {nft.attack !== undefined && (
              <StatPill label="ATK" value={nft.attack} />
            )}
            {nft.defence !== undefined && (
              <StatPill label="DEF" value={nft.defence} />
            )}
          </Box>
        )}

        {isListing && listing && (
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Box sx={priceValueSx}>
              {listing.priceInINC}
              <Box
                component="span"
                sx={{ ml: 0.5, font: `700 11px ${MONO}`, color: '#5c6b78' }}
              >
                INC
              </Box>
            </Box>
            <Typography sx={{ mt: 0.5, ...subSx }}>
              ~${listing.priceInUSD} USD · {listing.seller.username}
            </Typography>

            {listing.seller.username !== currentUsername && (
              <Button
                variant="contained"
                fullWidth
                disableElevation
                data-track="marketplace.buy_now_click"
                sx={{ ...cyanBtnSx, mt: 1.5 }}
                onClick={() => onBuyNow(listing)}
                startIcon={<CartIcon />}
              >
                Buy Now
              </Button>
            )}
          </Box>
        )}

        {!isListing && data.owned > 0 && (
          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Typography sx={{ ...subSx, mb: 1 }}>
              You own{' '}
              <Box component="span" sx={{ color: '#e8f4fb', fontWeight: 700 }}>
                {data.owned}
              </Box>
            </Typography>
            <Button
              variant="contained"
              fullWidth
              disableElevation
              data-track="marketplace.list_click"
              sx={cyanBtnSx}
              onClick={() => onListForSale(data as UserNft)}
              startIcon={<SellIcon />}
            >
              List for Sale
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
